import React, { useContext, useEffect, useRef, useState } from "react";
import Text_Editor from "../../components/common/TextEditor"; // Import your Editor component
import { useNavigate, useLocation } from "react-router-dom";
import { ERROR_MESSAGES } from "../../components/common/GlobalMassssage";
import CopyToClipboard from "../../components/common/CopyToClipBoard/CopyToClipBoard";
import CommonClientVariables from "../../components/common/Variables";
import { AuthContext, LoaderContext } from "../../Context/ContextFunctions";
import {
  AddUpdateEmailTemplate,
  GetEmailTemplateModel,
} from "../../services/EmailTemplate/EmailTemplateApi";

// Dummy API functions. Replace with actual API calls.

const TemplatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ModuleName = "Predefined";

  // State declarations
  const [templateName, setTemplateName] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [requireErrorMessage, setRequireErrorMessage] = useState(false);
  const [modelAction, setModelAction] = useState("Add");
  const [modelRequestData, setModelRequestData] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isAddUpdateDone, setIsAddUpdateDone] = useState(false);
  const [showSuccessPopupModal, setShowSuccessPopupModal] = useState(false);

  // Example authDetails, replace/derive from context or props as needed
  const { setLoader } = useContext(LoaderContext);
  const { authDetails, pageSize, PanalModuleName } = useContext(AuthContext);

  // Example states for fetched data (from GetEmailTemplateModel response)
  const [clientObj, setClientObj] = useState(null);
  const [project, setProject] = useState(null);
  const [serviceKeyIDs, setServiceKeyIDs] = useState(null);
  const [engagementModel, setEngagementModel] = useState(null);
  const [contacts, setContacts] = useState(null);

  // You mentioned PanalModuleName, assuming a constant or prop, define dummy here

  // Handle editor content change
  const handleContentChange = (content) => {
    setEditorContent(content);
  };

  // Load data on mount if update action
  useEffect(() => {
    // Defensive location.state check
    if (!location.state) {
      setModelAction("Add");
      return;
    }
    setModelAction(
      location.state.Action === null ? "Add" : location.state.Action
    );

    // Save the modelRequestData (contains KeyID etc)
    setModelRequestData(location.state);

    if (
      location.state.KeyID !== undefined &&
      location.state.KeyID !== null &&
      location.state.Action === "Update"
    ) {
      GetEmailTemplateModelData(location.state.KeyID);
    }
  }, [location.state]);

  // Fetch email template model data based on KeyID
  const GetEmailTemplateModelData = async (KeyID) => {
    setLoader(true);
    try {
      const data = await GetEmailTemplateModel(
        PanalModuleName,
        KeyID,
        authDetails.adminKeyID
      );
      if (data.statusCode === 200) {
        const ModalData = data.responseData.data;
        setClientObj({
          clientKeyID: ModalData.clientKeyID,
          businessTypeID: ModalData.businessTypeID,
          accountManagerUserKeyID: ModalData.accountManagerUserKeyID,
        });
        setProject(ModalData.project);
        setServiceKeyIDs(ModalData.serviceKeyIDs);
        setEngagementModel(ModalData.engagementModel);
        setContacts(ModalData.officerList);

        // Set templateName and editorContent from fetched data (assumed fields)
        setTemplateName(ModalData.templateName || "");
        setEditorContent(ModalData.content || "");
      }
    } catch (error) {
      console.error("Error fetching email template model data:", error);
    } finally {
      setLoader(false);
    }
  };

  // Submit or update template handler
  const handleUpdateTemplate = async () => {
    // Validate required fields
    if (!templateName || !editorContent) {
      setRequireErrorMessage(true);
      return false;
    }

    setRequireErrorMessage(false);
    setLoader(true);
    setErrorMessage("");
    try {
      // Call API to add or update template
      const data = await AddUpdateEmailTemplate({
        adminKeyID: authDetails.adminKeyID,
        emailTemplateKeyID: modelRequestData.KeyID || null, // null if adding new
        templateName: templateName,
        content: editorContent,
        ModuleName: ModuleName,
      });

      if (data?.statusCode === 200) {
        setIsAddUpdateDone(true);
        setShowSuccessPopupModal(true);
        setLoader(false);
        navigate("/emailTemplate");
      } else {
        setLoader(false);
        const errorMsg =
          data?.response?.data?.errorMessage ||
          "An error occurred saving template";
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      setLoader(false);
      setErrorMessage("An error occurred: " + error.message);
      console.error("Error in AddUpdateEmailTemplate:", error);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg mt-10 flex flex-col h-[70vh]">
      {/* Header */}
      <div className="p-6 border-b">
        <h2 className="text-xl font-semibold">{modelAction} Template</h2>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Template Name */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Template Name
          </label>
          <input
            type="text"
            value={templateName}
            maxLength={50}
            onChange={(e) => {
              let value = e.target.value;
              // Prevent input starting with number or space
              if (value === "") {
                setTemplateName("");
                return;
              }
              if (/^[\d\s]/.test(value)) {
                // Ignore update if first char is digit or space
                return;
              }
              // Capitalize first letter, keep rest as is
              value = value.charAt(0).toUpperCase() + value.slice(1);
              setTemplateName(value);
            }}
            placeholder="Enter Template Name"
            className="w-full border p-2 rounded"
          />
          {requireErrorMessage && (!templateName || templateName === "") && (
            <label className="text-red-500 text-xs mt-1">
              {ERROR_MESSAGES}
            </label>
          )}
        </div>

        {/* Variables Section */}
        <div>
          <fieldset className="border border-gray-300 rounded-md p-4 relative">
            <legend className="text-sm font-semibold text-gray-700 px-2">
              Variables
            </legend>

            <CopyToClipboard
              texts={CommonClientVariables.CommonClientVariables}
            />
          </fieldset>
        </div>

        {/* Text Editor */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Template Content
          </label>
          <Text_Editor
            index={0}
            handleContentChange={handleContentChange}
            editorState={editorContent}
            modelAction={modelAction === "Update"}
          />
          {requireErrorMessage && (!editorContent || editorContent === "") && (
            <label className="text-red-500 text-xs mt-1">
              {ERROR_MESSAGES}
            </label>
          )}
        </div>

        {/* Show error message if API returns error */}
        {errorMessage && (
          <div className="text-red-500 text-sm mt-1 font-medium">
            {errorMessage}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t flex justify-end">
        <button
          onClick={() => navigate("/configure")}
          className="mx-2 px-4 py-2 bg-gray-300 rounded-md text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleUpdateTemplate}
          className="px-4 py-2 text-white bg-[#E86B1B] rounded-md text-sm"
        >
          Submit Template
        </button>
      </div>
    </div>
  );
};

export default TemplatePage;
